import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { widthToColumns } from '@leadgab/shared';
import { Modal } from '../Modal.tsx';
import { Select } from '../Select.tsx';
import { Field, Spinner } from '../ui.tsx';
import { cellText, parseCell } from '../../lib/inventory.ts';
import type { CellValue, FieldRow, ItemRow } from '../../lib/inventory.ts';

/**
 * Add or edit one item.
 *
 * Every control here comes from a field the workspace declared — there is no
 * list of inputs in this file, only a switch on `kind`. Adding a field to a
 * workspace adds a row to this form with no code change.
 */

interface ItemDialogProps {
  open: boolean;
  fields: FieldRow[];
  item: ItemRow | null;
  labelSingular: string;
  saving: boolean;
  error?: string;
  fieldErrors: Record<string, string>;
  onClose: () => void;
  onSubmit: (input: { externalId: string | null; data: Record<string, CellValue> }) => void;
}

function draftOf(fields: FieldRow[], item: ItemRow | null): Record<string, string> {
  const draft: Record<string, string> = {};
  for (const field of fields) draft[field.key] = cellText(item?.data[field.key]);
  return draft;
}

export function ItemDialog({
  open,
  fields,
  item,
  labelSingular,
  saving,
  error,
  fieldErrors,
  onClose,
  onSubmit,
}: ItemDialogProps): React.JSX.Element {
  const live = fields.filter((field) => !field.archived);

  const [draft, setDraft] = useState<Record<string, string>>(() => draftOf(live, item));
  const [externalId, setExternalId] = useState(item?.externalId ?? '');

  // Reset when the dialog is pointed at a different item — the component stays
  // mounted between opens, so without this the previous row's values persist.
  useEffect(() => {
    if (!open) return;
    setDraft(draftOf(live, item));
    setExternalId(item?.externalId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id, fields.length]);

  const set = (key: string, value: string): void => {
    setDraft((previous) => ({ ...previous, [key]: value }));
  };

  const submit = (): void => {
    const data: Record<string, CellValue> = {};
    for (const field of live) data[field.key] = parseCell(field.kind, draft[field.key] ?? '');

    onSubmit({ externalId: externalId.trim() || null, data });
  };

  return (
    <Modal
      open={open}
      size="lg"
      title={item ? `Edit ${labelSingular.toLowerCase()}` : `Add ${labelSingular.toLowerCase()}`}
      description={
        live.length === 0
          ? 'This inventory has no fields yet. Add some on the Fields tab first.'
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="lg-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="item-form"
            className="lg-btn-primary"
            disabled={saving || live.length === 0}
          >
            {saving ? <Spinner /> : null}
            {item ? 'Save changes' : `Add ${labelSingular.toLowerCase()}`}
          </button>
        </>
      }
    >
      <form
        id="item-form"
        className="lg-field-grid"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {error ? (
          <p
            className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/50 dark:text-rose-200"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        {/* Each field claims the share of the row its owner gave it. The grid is
            twelve columns, so 33% is four of them and three such fields land in
            a line. Below the breakpoint the CSS forces everything full width —
            a third of a phone screen is not an input. */}
        {live.map((field) => (
          <div key={field.key} style={{ '--span': widthToColumns(field.width) } as CSSProperties}>
            <Field
              label={field.required ? `${field.label} *` : field.label}
              htmlFor={`item-${field.key}`}
              error={fieldErrors[field.key]}
            >
              {field.kind === 'select' ? (
                <Select
                  id={`item-${field.key}`}
                  value={draft[field.key] ?? ''}
                  options={[
                    { value: '', label: '—' },
                    ...field.options.map((option) => ({ value: option, label: option })),
                  ]}
                  onChange={(value) => {
                    set(field.key, value);
                  }}
                />
              ) : (
                <input
                  id={`item-${field.key}`}
                  className="lg-input"
                  // `date` and `number` get the right keyboard and picker for
                  // free; text is the fallback and covers everything else.
                  type={field.kind === 'date' ? 'date' : field.kind === 'number' ? 'number' : 'text'}
                  inputMode={field.kind === 'number' ? 'decimal' : undefined}
                  value={draft[field.key] ?? ''}
                  onChange={(event) => {
                    set(field.key, event.target.value);
                  }}
                />
              )}
            </Field>
          </div>
        ))}

        <div>
          <Field
            label="External id"
            htmlFor="item-external-id"
            error={fieldErrors.externalId}
            hint="Optional. Your own identifier, so re-importing a feed updates this row instead of adding another."
          >
            <input
              id="item-external-id"
              className="lg-input"
              value={externalId}
              onChange={(event) => {
                setExternalId(event.target.value);
              }}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
